import React, { useState, useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Dialog, Paper, IconButton, Typography } from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import styles from "../../../Audits/Review/components/styles";
import { strings } from "../../../common/i18n";
import newStyles from "./styles";

const AUTO_HIDE_DURATION = 2000;

const DraggableImageControls = ({
    imageUrl,
    rotation = 0,
    onRotationChange
}) => {
    const classes = makeStyles(styles)();
    const [angle, setAngle] = useState(rotation);
    const [buttonDisable, setButtonDisable] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const idRef = useRef();
    const isPDF = imageUrl?.toLowerCase().endsWith(".pdf");

    // Drag state
    const [position, setPosition] = useState({ x: 200, y: 20 });
    const [dragging, setDragging] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    // Rotation timer
    const timer = () => {
        const timeout = setTimeout(
            () => setButtonDisable(false),
            AUTO_HIDE_DURATION
        );
        idRef.current = timeout;
    };

    const rotateRight = (e) => {
        e.stopPropagation();
        setButtonDisable(true);
        const newAngle = (angle + 90) % 360;
        setAngle(newAngle);
        onRotationChange?.({ rotation: newAngle });
        timer();
    };

    const rotateLeft = (e) => {
        e.stopPropagation();
        setButtonDisable(true);
        const newAngle = (angle - 90 + 360) % 360;
        setAngle(newAngle);
        onRotationChange?.({ rotation: newAngle });
        timer();
    };

    useEffect(() => {
        setAngle(rotation);
        return () => clearTimeout(idRef.current);
    }, [rotation, imageUrl]);

    // Drag handlers
    const handleMouseDown = (e) => {
        setDragging(true);
        setOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e) => {
        if (!dragging) return;
        setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const handleMouseUp = () => setDragging(false);

    return (
        <>
            {/* Thumbnail / Preview box to open dialog */}
            <Box
                className={classes.imageController}
                onClick={() => setDialogOpen(true)}
            >
                {!buttonDisable && (
                    <RotateLeftIcon
                        sx={newStyles.rotateLeftIcon}
                        onClick={rotateLeft}
                    />
                )}
                {isPDF ? (
                    <iframe
                        src={imageUrl}
                        width="100%"
                        height="600px"
                        title="PDF Viewer"
                        style={{ border: "none" }}
                    />
                ) : (
                    <Box
                        sx={{
                            ...newStyles.singleImageView,
                            backgroundImage: `url(${imageUrl})`,
                            transform: `rotate(${angle}deg)`
                        }}
                    />
                )}
                {!buttonDisable && (
                    <RotateRightIcon
                        sx={newStyles.rotateRightIcon}
                        onClick={rotateRight}
                    />
                )}
            </Box>

            {/* Draggable Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth={false}
                PaperComponent={(props) => (
                    <Paper
                        {...props}
                        sx={{
                            ...newStyles.paperContainer,
                            transform: `translate(${position.x}px, ${position.y}px)`
                        }}
                        title={strings("messages.draggableTitle")}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                    >
                        {/* Draggable Header */}
                        <Box
                            onMouseDown={(e) => {
                                if (e.target.closest("button")) return; // ignore buttons
                                handleMouseDown(e);
                            }}
                            sx={newStyles.dialogHeader}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                            >
                                {strings("imagePreview")}
                            </Typography>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDialogOpen(false);
                                }}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Dialog Content */}
                        <Box sx={newStyles.dialogContent}>
                            {isPDF ? (
                                <iframe
                                    src={imageUrl}
                                    width="800px"
                                    height="600px"
                                    style={{ border: "none" }}
                                    title={strings("pdfViewer")}
                                />
                            ) : (
                                <Box
                                    style={{
                                        position: "relative",
                                        display: "inline-block"
                                    }}
                                >
                                    <img
                                        src={imageUrl}
                                        alt={strings("imagePreview")}
                                        style={{
                                            ...newStyles.previewImage,
                                            transform: `rotate(${angle}deg)`
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Paper>
                )}
            />
        </>
    );
};

export default DraggableImageControls;
